import { useApolloClient } from "@apollo/client";
import { getDataOrNull } from "@msys/common";
import { DebouncedSearchInput, ListHeader, ModalOpenButton } from "@msys/ui";
import { useTranslate } from "@tolgee/react";
import React from "react";
import { useNavigate } from "react-router-dom";
import { namedOperations } from "../../../clients/graphqlTypes.js";
import { RestrictedByOrganisationPermissionWithDebug } from "../../auth/RestrictedByOrganisationPermission.js";
import { ButtonCreate } from "../../commons/button/Button.js";
import { SwitchCollectionViewButton } from "../../commons/button/SwitchCollectionViewButton.js";
import { CollectionView } from "../../commons/hooks/useCollectionView.js";
import { PageTopbarItem } from "../../commons/layout/Page.js";
import { Page } from "../../commons/layout/Page.js";
import { PageContainer } from "../../commons/layout/PageContainer.js";
import { CreateProductListModal } from "../../features/productLists/CreateProductListModal.js";
import { FilterByProductListAuthorsButton } from "../../features/productLists/FilterByProductListAuthorsButton.js";
import { ProductListActionsMenu } from "../../features/productLists/ProductListActionsMenu.js";
import { ProductListsCollectionView } from "../../features/productLists/ProductListsCollectionView.js";
import {
  ProductListsFilters,
  useProductListsList,
} from "../../features/productLists/useProductListsList.js";
import { useDataGridStateStore } from "../../features/users/useDataGridStateStore.js";
import {
  ProductListsQueryVariables,
  useProductListsQuery,
} from "./ProductLists.generated.js";

const ALLOWED_VIEWS: CollectionView[] = ["gallery", "list", "table"];

interface Props {
  submenuItems: PageTopbarItem[];
  pathToProductListsList: string;
}

export const ProductLists = ({
  submenuItems,
  pathToProductListsList,
}: Props) => {
  const navigate = useNavigate();

  const { t } = useTranslate(["ProductLists", "Global"]);

  const {
    paginationModel,
    setPaginationModel,
    filters,
    setFilters,
    sorting,
    setSorting,
    activeView,
    setActiveView,
    queryBaseVariables,
  } = useProductListsList<
    ProductListsFilters & Pick<ProductListsQueryVariables, "offset" | "limit">
  >({
    activeViewStorageKey: "product-lists",
    activeViewInitialDesktopValue: "table",
    activeViewInitialMobileValue: "list",
    initialFilters: {},
  });

  // Preview images are only shown in the media views, so the table view skips
  // the per-list PIM resolution behind them.
  const withImages = activeView !== "table";

  const client = useApolloClient();
  const query = useProductListsQuery({
    client,
    variables: {
      ...queryBaseVariables,
      sorting,
      searchTerm: filters.searchTerm,
      filters: {
        authorIds: filters.authorIds,
      },
      withImages,
    },
  });

  const productLists = getDataOrNull(
    (query.data ?? query.previousData)?.productLists
  );
  const items = productLists?.edges.map(e => e.node) ?? [];
  const totalCount = productLists?.totalCount ?? 0;

  const stateStore = useDataGridStateStore("ProductLists");

  return (
    <Page
      subtitle={t("Product lists", { ns: "ProductLists" })}
      submenuItems={submenuItems}
    >
      <PageContainer>
        <ListHeader
          SearchField={
            <DebouncedSearchInput
              placeholder={t("Search", { ns: "Global" })}
              defaultValue={filters.searchTerm}
              onChangeSearchTerm={newValue =>
                setFilters(filters => ({
                  ...filters,
                  searchTerm: newValue,
                }))
              }
            />
          }
          FilterFields={
            <FilterByProductListAuthorsButton
              inputLabel={t("Created by", { ns: "Global" })}
              placeholder={t("Search by author", { ns: "ProductLists" })}
              value={filters.authorIds ?? []}
              setValue={newValue =>
                setFilters(filters => ({
                  ...filters,
                  authorIds: newValue,
                }))
              }
            />
          }
          SwitchViewButton={
            <SwitchCollectionViewButton
              allowedViews={ALLOWED_VIEWS}
              activeView={activeView}
              setActiveView={setActiveView}
            />
          }
          CreateButton={
            <RestrictedByOrganisationPermissionWithDebug permission="pim_catalogue_update">
              <ModalOpenButton
                Modal={CreateProductListModal}
                modalProps={{
                  handleComplete: productListId => {
                    navigate(`${pathToProductListsList}/${productListId}`);
                  },
                  refetchQueries: [
                    namedOperations.Query.ProductLists,
                    namedOperations.Query.ProductListsAuthors,
                  ],
                }}
              >
                <ButtonCreate
                  title={t("New product list", {
                    ns: "ProductLists",
                  })}
                />
              </ModalOpenButton>
            </RestrictedByOrganisationPermissionWithDebug>
          }
          sx={{ mb: 2 }}
        />
        <ProductListsCollectionView
          items={items}
          totalCount={totalCount}
          activeView={activeView}
          loading={query.loading}
          paginationModel={paginationModel}
          setPaginationModel={setPaginationModel}
          sorting={sorting}
          setSorting={setSorting}
          stateStore={stateStore}
          getProductListTo={productList =>
            `${pathToProductListsList}/${productList.id}`
          }
          RowActions={ProductListActionsMenu}
        />
      </PageContainer>
    </Page>
  );
};
